import { Box, Button, Flex, Icon } from '@inflearn/ds-react';
import { faStar } from '@inflearn/pro-regular-svg-icons';
import {
  faAngleRight,
  faFilePen,
  faPen,
  faStar as faSolidStar,
} from '@inflearn/pro-solid-svg-icons';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

import useMediaQuery from '../../../../../styles/useMediaQuery';
import { useCourseReviewContext } from '../CourseCardContainer';
import type { CourseCardData } from './courseCardDataTransformer';

export function FeedbackButtons({
  isReviewable,
  review,
  voucherId,
  course,
}: {
  isReviewable: boolean;
  review?: CourseCardData['review'];
  voucherId: number;
  course: CourseCardData['course'];
}) {
  const { t } = useTranslation();
  const { openReviewModal } = useCourseReviewContext();
  const { isMobile } = useMediaQuery();

  const buttonSize = isMobile ? 'xs' : 'sm';

  const onClick = () => {
    openReviewModal(voucherId);
  };

  if (course.isOfflineCourse && course.offlineCourse) {
    const { hasSurveyAnswer, canAnswerSurvey, surveyUrl, surveyReadonlyUrl } = course.offlineCourse;

    if (!hasSurveyAnswer && canAnswerSurvey) {
      return (
        <Flex gap="sm">
          <Button
            component={Link}
            target="_blank"
            href={surveyUrl}
            leftIcon={<Icon icon={faFilePen} size="sm" />}
            variant="default"
            color="infgreen"
            size={buttonSize}
            radius="md"
          >
            {t('설문 작성')}
          </Button>
        </Flex>
      );
    }

    if (!hasSurveyAnswer) {
      // 배치 때문
      return <Box />;
    }

    return (
      <Flex gap="sm">
        <Button
          component={Link}
          target="_blank"
          href={canAnswerSurvey ? surveyUrl : surveyReadonlyUrl}
          leftIcon={canAnswerSurvey ? <Icon icon={faPen} size="sm" /> : undefined}
          variant="default"
          color="infgreen"
          size={buttonSize}
          radius="md"
        >
          {canAnswerSurvey ? t('설문 수정') : t('설문 보기')}
        </Button>
      </Flex>
    );
  }

  if (isReviewable) {
    return (
      <Flex gap="sm">
        <Button
          onClick={onClick}
          leftIcon={<Icon icon={faStar} size="sm" color="gray.6" />}
          variant="default"
          color="infgreen"
          size={buttonSize}
          radius="md"
        >
          {t('수강평 작성')}
        </Button>
      </Flex>
    );
  }

  if (review) {
    return (
      <Flex gap="sm">
        <Button
          onClick={onClick}
          aria-label={t('수강평 수정')}
          leftIcon={<Icon icon={faSolidStar} color="yellow.6" size="sm" />}
          rightIcon={<Icon icon={faAngleRight} color="gray" size="sm" />}
          variant="default"
          color="infgreen"
          size={buttonSize}
          radius="md"
        >
          {review.rating.toFixed(1)}
        </Button>
      </Flex>
    );
  }

  return null;
}
